<template>
    <section>
        <ds-modal
            :is-open="isNotesModalOpen"
            :sub-title="subTitle"
            :title.sync="modalTitle"
            show-close
            @close="modalClose"
        >
            <div class="layout">
                <div>
                    <ds-input-field
                        v-model="notes.title"
                        :submitted="submitted"
                        label="Title"
                        name="Title"
                        required
                        data-qa="note-title"
                    />

                    <ds-text-area-field
                        v-if="forceRender"
                        v-model="notes.contents"
                        type="text"
                        name="Description"
                        label="Description"
                        class="notes-description"
                        :maxlength="null"
                        auto-grow
                    />

                    <div class="moreOptions">
                        <ds-text-button
                            dense
                            :trailing-icon="!moreOptions ? 'chevron-down' : 'chevron-up'"
                            @click="toggleMoreOptions"
                        >
                            More options
                        </ds-text-button>
                    </div>

                    <div v-if="moreOptions" data-qa="more-options">
                        <ds-select-field
                            v-model="notes.templateId"
                            :options="templateObject"
                            :readonly="templateNotEditable"
                            bind-value-only
                            label="Apply template"
                            name="Apply template"
                            data-qa="apply-template"
                            @input="getFieldsFromTemplateId"
                        />

                        <ds-select-field
                            v-model="notes.actionType"
                            :options="actionTypeDropDown"
                            bind-value-only
                            label="Note type"
                            name="Note template"
                        />

                        <ds-multiselect
                            v-model="notes.assignedTo"
                            :options="ownersDrop"
                            bind-value-only
                            searchable
                            placeholder="Assigned to"
                            label="Assigned to"
                            name="Assigned to"
                        />

                        <ds-multiselect
                            v-model="notes.userIds"
                            :options="ownersDrop"
                            placeholder="Notify users"
                            bind-value-only
                            label="Notify users"
                            multiple
                            name="Notify users"
                            searchable
                        />

                        <ds-checkbox v-model="notes.notifyBCC">
                            Send as a blind carbon copy
                        </ds-checkbox>
                    </div>

                    <div>
                        <ds-button
                            :disabled="disableSave"
                            :loading="saving"
                            data-qa="save-note"
                            @click="modalAction"
                        >
                            Save
                        </ds-button>

                        <span class="button-space" />

                        <ds-outline-button
                            v-if="notes.id>0"
                            data-qa="delete-note"
                            destructive
                            @click="modalDelete"
                        >
                            Delete
                        </ds-outline-button>
                    </div>
                </div>
            </div>
        </ds-modal>
    </section>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        contact: Object,
        userId: [String, Number],
        noteDropdowns: Object,
    },

    data() {
        return {
            isNotesModalOpen: false,
            forceRender: true,
            submitted: false,
            modalTitle: 'Add a note',
            moreOptions: false,
            subTitle: '',
            showAction: true,
            options: {},
            saving: false,
            notes: {
                contactId: 0,
                actionType: '',
                title: '',
                contents: '',
                assignedTo: 0,
                templateId: 0,
                userIds: [],
                notifyBCC: false,
                id: 0,
            },
            actionTypeDropDown: [],
            templateObject: [],
            templateNotEditable: false,
            nestedPopUp: false,
        };
    },

    mounted() {
        this.$root.$on('DROPDOWN_LOADED', (data) => {
            this.options = data;
        });
    },

    created() {
        this.notes.contactId = Number(this.contact.id);
        this.subTitle = this.fullName;
        this.notes.assignedTo = Number(this.userId);
    },

    beforeDestroy() {
        this.$root.$off('DROPDOWN_LOADED');
    },

    computed: {
        disableSave() {
            return !this.notes.title;
        },

        ownersDrop() {
            return this.options.owners;
        },

        fullName() {
            return `${this.contact.firstName} ${this.contact.lastName}`;
        },
    },

    methods: {
        async modalDelete() {
            const successMessage = 'Note deleted successfully';

            try {
                await axios.delete(`/app/note/deleteNote/${this.notes.id}`);
                this.$toast({ message: successMessage });

                if (this.nestedPopUp) {
                    this.$root.$emit('NOTES_STATUS_POPUP', true);
                }
                this.$root.$emit('NOTES_STATUS', true);

                this.modalClose();
            } catch (error) {
                this.$error({ message: 'error occured while deleting note' });
            }
        },

        async getFieldsFromTemplateId() {
            try {
                const response = await axios.get(`/app/note/getTemplateActionFields/${this.notes.templateId}/${this.contact.id}`);

                this.notes.actionType = response.data.actionType;
                this.notes.contents = response.data.contents;
                this.notes.title = response.data.title;
                this.notes.assignedTo = response.data.assignedTo;
                this.notes.templateId = response.data.templateId;
                this.notes.userIds = response.data.userIds;
                this.notes.notifyBCC = response.data.notifyBCC;
                this.$root.$emit('NOTES_STATUS', true);
            } catch (error) {
                this.$error({ message: 'error occured while getting note by Template Id' });
            }
        },

        showModal(note, nestedPopUp) {
            this.nestedPopUp = nestedPopUp;
            const noteType = this.noteDropdowns.noteTypes;

            this.actionTypeDropDown = noteType && Object.keys(noteType).map((key) => {
                return { label: key, value: key };
            }).sort((prev, next) => prev.label.localeCompare(next.label));

            const templateDrop = this.noteDropdowns.templateList;

            this.templateObject = templateDrop && Object.keys(templateDrop).map((key) => {
                return { label: templateDrop[key], value: Number(key) };
            }).sort((prev, next) => prev.label.localeCompare(next.label));

            if (note != null) {
                this.modalTitle = 'Edit note';
                this.templateNotEditable = true;
                this.moreOptions = false;
                this.showAction = true;
                this.notes.id = note.id;
                this.notes.actionType = note.actionType;
                this.notes.contents = note.contents;
                this.notes.title = note.title;
                this.notes.assignedTo = note.assignedTo;
                this.notes.templateId = note.templateId;
            }
            this.notes.contactId = Number(this.contact.id);
            this.isNotesModalOpen = true;
        },

        toggleMoreOptions() {
            this.moreOptions = !this.moreOptions;
        },

        async modalAction() {
            if (this.notes.title !== '') {
                this.saving = true;

                if (this.notes.userIds == null) {
                    this.notes.userIds = [];
                }
                const successMessage = this.notes.id > 0 ? 'Note updated successfully' : 'Note added successfully';

                try {
                    await axios.post('/app/note/addNote', this.notes);

                    this.$toast({ message: successMessage });

                    if (this.nestedPopUp) {
                        this.$root.$emit('NOTES_STATUS_POPUP', true);
                    }
                    this.$root.$emit('NOTES_STATUS', true);

                    this.modalClose();
                } catch (error) {
                    const errorMessage = this.notes.id > 0 ? 'error while updating note' : 'error while added note';

                    this.$error({ message: errorMessage });
                }
            } else {
                this.saving = false;
                this.submitted = true;
            }
        },

        modalClose() {
            this.nestedModal_reset();
            this.isNotesModalOpen = false;
        },

        nestedModal_reset() {
            this.notes.assignedTo = Number(this.userId);
            this.notes.actionType = '';
            this.notes.title = '';
            this.notes.contents = '';
            this.modalTitle = 'Add a note';
            this.moreOptions = false;
            this.notes.userIds = [];
            this.notes.id = 0;
            this.notes.templateId = 0;
            this.templateNotEditable = false;
            this.notes.contactId = Number(this.contact.id);
            this.saving = false;
            this.forceRender = false;
            this.$nextTick(() => {
                this.forceRender = true;
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.chevron {
    bottom: -$spacing-050;
    position: relative;
    --icon-size: 1.25rem;
}

.moreOptions {
    display: flex;
    margin: auto;
    margin-bottom: $spacing-400;
    margin-top: $spacing-100;
    justify-content: center;
}

.layout {
    display: flex;
    flex-direction: column;
}

.notes-description {
    --textarea-min-height: #{px-to-rem(200px)};
}

.button-space {
    margin-left: $spacing-200;
}
</style>
